import React, { useState } from "react";
import {
  Edit,
  Visibility,
} from "@mui/icons-material";
import TableComponent from "../TableComponent/TableComponent";
import { useQuery } from "react-query";
import { QK } from "../../base/qk";

import HouseKeepingConfirmModal from "./modals/HouseKeepingConfirmModal";
import CustomDate from "../CustomForm/CustomDate";
import { getVisitTimeline } from "../../api/services/visitplan";
import NotFound from "../NotFound/NotFound";
import TaskDetailsModal from "../PopupModal/TaskDetailsModal";
import { formatDate, formatLondonDate } from "../../utils/time";

const toTimeString = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DailyJobs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(
    formatLondonDate(new Date(), "yyyy-MM-dd")
  );
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const jobPerPage = 30;

  const { data: dailyJobs, isLoading } = useQuery(
    [QK.dailyJobs, currentPage, selectedDate],
    () =>
      getVisitTimeline(currentPage, "", "", "", "", "", "", "", selectedDate),
    { keepPreviousData: true, enabled: !!currentPage || !!selectedDate }
  );

  const totalRecords = dailyJobs?.count || 0;
  const totalPages = Math.ceil(totalRecords / jobPerPage);

  const handlePageChange = (event, page) => setCurrentPage(page);

  const handleShowJobModal = (jobId) => {
    setSelectedJob(jobId);
    setShowJobModal(true);
  };

  const handleShowTaskDetailsModal = (jobId) => {
    setSelectedJob(jobId);
    setShowTaskDetailsModal(true);
  };
  const columns = [
    { field: "id", headerName: "Job ID" },
    { field: "buildDate", headerName: "Date" },
    { field: "scheduledTime", headerName: "Scheduled Time" },
    { field: "actualTime", headerName: "Actual Time(s)" },
    { field: "jobName", headerName: "Job Name" },
    { field: "serviceUser", headerName: "Service User" },
    { field: "employee", headerName: "Employee(s)" },
    { field: "status", headerName: "Status" },
  ];

  const formatTimeRange = (start, end) => {
    const formattedStart = toTimeString(start);
    const formattedEnd = toTimeString(end);
    return `${formattedStart} - ${formattedEnd}`;
  };

  const formatActualTimes = (employeeDetails) => {
    if (!employeeDetails || employeeDetails.length === 0) return "—";
    return employeeDetails
      .map((emp) => {
        const start = toTimeString(emp.actual_start);
        const end = toTimeString(emp.actual_end);
        return `${emp.first_name} ${emp.last_name}: ${start} - ${end}`;
      })
      .join("\n");
  };

  const data =
    dailyJobs?.results?.map((job) => ({
      id: job.id,
      buildDate: job.build_for ? formatDate(job.build_for) : "—",
      scheduledTime: formatTimeRange(job.scheduled_start, job.scheduled_end),
      actualTime: formatActualTimes(job.employee_details),
      jobName: job.visit_name,
      serviceUser: job.client_name,
      status: job.status,
      employee:
        job.employee_details?.length > 0
          ? job.employee_details
              .map((emp) => `${emp.first_name} ${emp.last_name}`)
              .join(", ")
          : "—",
    })) || [];

  const actions = [
    {
      icon: <Visibility />,
      handler: (row) => handleShowTaskDetailsModal(row.id),
      color: "error",
    },
    {
      icon: <Edit />,
      handler: (row) => handleShowJobModal(row.id),
      color: "primary",
    },
  ];

  return (
    <div>
      <div className="w-25 ml-3">
        <CustomDate
          label="Select Date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          disabledAfter={formatLondonDate(new Date(), "yyyy-MM-dd")}
        />
      </div>
      <div style={{ padding: "20px" }}>
        {dailyJobs?.results?.length === 0 ? (
          <NotFound></NotFound>
        ) : (
          <TableComponent
            columns={columns}
            data={data}
            actions={actions}
            isLoading={isLoading}
            pagination={{ totalPages, currentPage }}
            onPageChange={handlePageChange}
          />
        )}
        {showJobModal && (
          <HouseKeepingConfirmModal
            jobId={selectedJob}
            open={showJobModal}
            handleClose={() => setShowJobModal(false)}
          />
        )}
        {showTaskDetailsModal && (
          <TaskDetailsModal
            id={selectedJob}
            show={showTaskDetailsModal}
            handleClose={() => setShowTaskDetailsModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DailyJobs;